import { useState } from "react";
import {
  deleteUser,
  EmailAuthProvider,
  reauthenticateWithCredential,
  GoogleAuthProvider,
  reauthenticateWithPopup,
} from "firebase/auth";

import { auth } from "../../firebase";

import { db } from "../../firebase";

import { deleteDoc, doc, getDocs, collection } from "firebase/firestore";
import { deleteProfilePhoto } from "../../utils/indexedDB";

/* ================= FILTER LOGIC ================= */

function getFilteredHistory({
  testHistory,
  resetType,
  selector,
  customMode,
  from,
  to,
}) {
  let combined = 0;
  let normal = 0;
  let stream = 0;

  return testHistory.filter((t) => {
    if (resetType !== "all" && t.testType !== resetType) return false;

    let testNo;
    if (resetType === "all") testNo = ++combined;
    else if (t.testType === "normal") testNo = ++normal;
    else testNo = ++stream;

    if (selector === "all") return true;

    if (customMode === "id") {
      if (from && testNo < Number(from)) return false;
      if (to && testNo > Number(to)) return false;
    }

    if (customMode === "date") {
      const d = new Date(t.date);
      if (from && d < new Date(from)) return false;
      if (to && d > new Date(to)) return false;
    }

    return true;
  });
}

/* ================= COMPONENT ================= */

export default function Reset({
  resetAll,
  testHistory,
  setTestHistory,
  sidebarTheme,
  exportFilteredHistory,
  isAuthenticated,
}) {
  const [resetType, setResetType] = useState("all");
  const [selector, setSelector] = useState("all");
  const [customMode, setCustomMode] = useState("id");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  // 🔴 CONFIRMATION POPUP STATE
  const [confirm, setConfirm] = useState(null);
  // confirm = { title, message, action }

  const dangerBtn = {
    padding: "8px 18px",
    borderRadius: 999,
    background: "#ef4444",
    color: "#fff",
    border: "none",
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
  };

  const purpleBtn = {
    padding: "8px 18px",
    borderRadius: 999,
    background: sidebarTheme.accentGradient,
    color: "#fff",
    border: "none",
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
  };

  const pill = {
    padding: "8px 14px",
    borderRadius: 999,
    border: `1px solid ${sidebarTheme.border}`,
    background:
      sidebarTheme.mode === "dark"
        ? "#020617" // slate-950
        : "#ffffff",
    color: sidebarTheme.text,
    fontSize: 13,
  };

  const handleDeleteAccount = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      const providerId = user.providerData[0]?.providerId;

      /* ================= RE-AUTH ================= */

      if (providerId === "password") {
        const password = prompt(
          "For security reasons, please enter your password to delete your account."
        );
        if (!password) return;

        const credential = EmailAuthProvider.credential(user.email, password);

        await reauthenticateWithCredential(user, credential);
      } else {
        const provider = new GoogleAuthProvider();
        await reauthenticateWithPopup(user, provider);
      }

      /* ================= DELETE FIRESTORE DATA ================= */

      const uid = user.uid;

      const testsSnap = await getDocs(collection(db, "users", uid, "tests"));
      for (const d of testsSnap.docs) {
        await deleteDoc(d.ref);
      }

      await deleteDoc(doc(db, "users", uid, "profile", "main"));
      await deleteDoc(doc(db, "users", uid, "settings", "main"));

      /* ================= DELETE AUTH ================= */

      await deleteUser(user);

      /* ================= CLEAN LOCAL ================= */

      localStorage.clear();
      await deleteProfilePhoto();

      alert("Your account has been deleted successfully.");
      window.location.reload();
    } catch (err) {
      console.error(err);

      if (err.code === "auth/wrong-password") {
        alert("Incorrect password. Account not deleted.");
      } else if (err.code === "auth/popup-closed-by-user") {
        alert("Re-authentication cancelled.");
      } else {
        alert("Failed to delete account. Please try again.");
      }
    }
  };

  return (
    <div>
      <h1 style={{ fontSize: 34, color: "#ef4444", marginBottom: 24 }}>
        🧹 Reset Center
      </h1>

      {/* ================= RESET TEST HISTORY ================= */}
      {isAuthenticated && (
        <div style={card(sidebarTheme)}>
          <strong style={{ fontSize: 20 }}>Reset Test History</strong>

          <p style={{ fontSize: 13, opacity: 0.7 }}>
            Deletes selected test history based on type and selection criteria.
          </p>

          <div
            style={{
              display: "flex",
              gap: 10,
              justifyContent: "center",
              marginTop: 8,
            }}
          >
            <button
              style={purpleBtn}
              onClick={() => {
                const data = getFilteredHistory({
                  testHistory,
                  resetType,
                  selector,
                  customMode,
                  from,
                  to,
                });

                if (data.length === 0) {
                  alert("No data to export.");
                  return;
                }

                exportFilteredHistory({
                  testHistory,
                  resetType,
                  selector,
                  customMode,
                  from,
                  to,
                });
              }}
            >
              Export History
            </button>

            <button
              style={dangerBtn}
              onClick={() => {
                const filtered = getFilteredHistory({
                  testHistory,
                  resetType,
                  selector,
                  customMode,
                  from,
                  to,
                });

                const deletable = filtered.filter((t) => t.id);

                if (deletable.length === 0) {
                  alert("No cloud-synced tests found to delete.");
                  return;
                }

                if (filtered.length === 0) {
                  alert("No tests match the selected filter.");
                  return;
                }

                setConfirm({
                  title: "Reset selected test history?",
                  message: "This action cannot be undone.",
                  action: async () => {
                    const uid = auth.currentUser.uid;

                    for (const t of deletable) {
                      if (t.id) {
                        await deleteDoc(doc(db, "users", uid, "tests", t.id));
                      }
                    }

                    setTestHistory((prev) =>
                      prev.filter((t) => !deletable.some((f) => f.id === t.id))
                    );
                  },
                });
              }}
            >
              Reset History
            </button>
          </div>

          <div style={{ display: "flex", gap: 16, marginTop: 16 }}>
            <div>
              <div>Type</div>
              <select
                value={resetType}
                onChange={(e) => setResetType(e.target.value)}
                style={pill}
              >
                <option value="all">All</option>
                <option value="normal">Normal</option>
                <option value="stream">Stream</option>
              </select>
            </div>

            <div>
              <div>Selector</div>
              <select
                value={selector}
                onChange={(e) => setSelector(e.target.value)}
                style={pill}
              >
                <option value="all">All</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            {selector === "custom" && (
              <>
                <button
                  style={{
                    ...pill,
                    background:
                      customMode === "id"
                        ? sidebarTheme.accent
                        : sidebarTheme.bgMain,
                    color: customMode === "id" ? "#fff" : "inherit",
                  }}
                  onClick={() => setCustomMode("id")}
                >
                  By Test #
                </button>

                <button
                  style={{
                    ...pill,
                    background:
                      customMode === "date"
                        ? sidebarTheme.accent
                        : sidebarTheme.bgMain,
                    color: customMode === "date" ? "#fff" : "inherit",
                  }}
                  onClick={() => setCustomMode("date")}
                >
                  By Date
                </button>
              </>
            )}
          </div>

          {selector === "custom" && (
            <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
              <input
                type={customMode === "date" ? "date" : "number"}
                placeholder="From"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                style={pill}
              />
              <input
                type={customMode === "date" ? "date" : "number"}
                placeholder="To"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                style={pill}
              />
            </div>
          )}
        </div>
      )}

      {/* ================= RESET ANALYSIS ================= */}
      {isAuthenticated && (
        <div style={card(sidebarTheme)}>
          <strong>Reset Analysis Only</strong>
          <p style={{ fontSize: 13, opacity: 0.7 }}>
            Analysis reset is unavailable. Analysis is generated dynamically
            from test history.
          </p>
          <button disabled style={{ ...pill, opacity: 0.5 }}>
            Reset Analysis
          </button>
        </div>
      )}

      {/* ================= CLEAR EVERYTHING ================= */}
      <div
        style={{
          ...card(sidebarTheme),
          border: "1px solid #ef4444",
          padding: 16,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div>
            <strong>⚠ Clear Everything</strong>

            <div style={{ fontSize: 12, opacity: 0.7 }}>
              Deletes profile, history, analysis, and resets settings.
            </div>
          </div>

          <button
            style={dangerBtn}
            onClick={() =>
              setConfirm({
                title: "Clear EVERYTHING?",
                message:
                  "This will permanently delete all data on this device.",
                action: async () => {
                  const user = auth.currentUser;

                  // 🔐 IF LOGGED-IN USER → CLEAR FIREBASE DATA
                  if (user) {
                    const uid = user.uid;

                    try {
                      // 1️⃣ DELETE ALL TEST HISTORY
                      const testsSnap = await getDocs(
                        collection(db, "users", uid, "tests")
                      );

                      for (const d of testsSnap.docs) {
                        await deleteDoc(d.ref);
                      }

                      // 2️⃣ DELETE PROFILE
                      await deleteDoc(doc(db, "users", uid, "profile", "main"));

                      // 3️⃣ DELETE SETTINGS
                      await deleteDoc(
                        doc(db, "users", uid, "settings", "main")
                      );
                    } catch (err) {
                      console.error("Firebase cleanup failed:", err);
                    }
                  }

                  // 🧹 CLEAR LOCAL DATA (GUEST + AUTH)
                  localStorage.clear();

                  // 🧹 CLEAR PROFILE PHOTO (IndexedDB)
                  await deleteProfilePhoto();

                  // 🔁 FORCE ONBOARDING AGAIN
                  localStorage.removeItem("onboardingDone");
                  localStorage.removeItem("profileData");
                  localStorage.removeItem("profilePhoto");

                  // 🔄 RESET APP STATE
                  resetAll();

                  // 🔄 HARD RELOAD
                  window.location.reload();
                },
              })
            }
          >
            CLEAR
          </button>
        </div>
      </div>

      {/* ================= DELETE ACCOUNT ================= */}
      {isAuthenticated && (
        <div style={{ ...card(sidebarTheme), border: "2px solid #b91c1c" }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <strong>🚫 Delete Account</strong>
            <button
              style={{ ...dangerBtn, background: "#b91c1c" }}
              onClick={() =>
                setConfirm({
                  title: "Account Verification Required",
                  message:
                    "For security reasons, deletion of account-linked data requires recent account verification.",
                  action: handleDeleteAccount,
                })
              }
            >
              DELETE
            </button>
          </div>
        </div>
      )}

      {/* ================= CONFIRMATION MODAL ================= */}
      {confirm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: sidebarTheme.card,
              padding: 24,
              borderRadius: 16,
              width: 360,
              textAlign: "center",
              border: `1px solid ${sidebarTheme.border}`,
            }}
          >
            <h3 style={{ marginBottom: 10 }}>{confirm.title}</h3>
            <p style={{ fontSize: 13, opacity: 0.75, marginBottom: 20 }}>
              {confirm.message}
            </p>

            <div style={{ display: "flex", justifyContent: "center", gap: 14 }}>
              <button
                style={{
                  padding: "8px 18px",
                  borderRadius: 999,
                  border: `1px solid ${sidebarTheme.border}`,
                  background: "transparent",
                  cursor: "pointer",
                }}
                onClick={() => setConfirm(null)}
              >
                Cancel
              </button>

              <button
                style={{
                  padding: "8px 18px",
                  borderRadius: 999,
                  background: "#ef4444",
                  color: "#fff",
                  border: "none",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
                onClick={async () => {
                  try {
                    await confirm.action();
                  } catch (err) {
                    console.error("Action failed:", err);
                    alert("Something went wrong. Please try again.");
                  } finally {
                    setConfirm(null);
                  }
                }}
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ================= STYLES ================= */

const card = (t) => ({
  padding: 20,
  borderRadius: 14,
  background: t.card,
  border: `1px solid ${t.border}`,
  marginBottom: 18,
  overflow: "hidden",
  boxSizing: "border-box",
});
